import React, { useMemo } from "react";
import { Plus, Trash2, Percent, Euro, Tag } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";

/**
 * OffersEditor — Éditeur inline de « multi-offres de remise » pour un code
 * (promo / affiliation / parrainage).
 *
 * Chaque offre :
 *  - `label` (optionnel, court, ex : « Pack 3 mois »)
 *  - `service_product_ids: string[]` — vide = applicable à tous les services
 *  - `discount_type` : `percentage` | `amount`
 *  - `discount_value` : nombre ≥ 0 (≤ 100 si percentage)
 *
 * Props :
 *  - `value` : list d'offres (état parent)
 *  - `onChange(nextOffers)` : callback
 *  - `services` : liste des services disponibles pour multi-select
 *              (filtrés par département par le parent)
 *  - `testid` : préfixe des data-testid (défaut « offer »)
 */
export default function OffersEditor({ value = [], onChange, services = [], testid = "offer" }) {
  const offers = Array.isArray(value) ? value : [];

  const setAt = (idx, patch) => {
    const next = offers.map((o, i) => (i === idx ? { ...o, ...patch } : o));
    onChange(next);
  };
  const removeAt = (idx) => onChange(offers.filter((_, i) => i !== idx));
  const add = () => onChange([
    ...offers,
    { label: "", service_product_ids: [], discount_type: "percentage", discount_value: 10 },
  ]);

  const svcById = useMemo(() => {
    const m = new Map();
    services.forEach((s) => m.set(s.id, s));
    return m;
  }, [services]);

  const toggleService = (idx, sid) => {
    const cur = offers[idx].service_product_ids || [];
    const next = cur.includes(sid) ? cur.filter((x) => x !== sid) : [...cur, sid];
    setAt(idx, { service_product_ids: next });
  };

  return (
    <div className="space-y-2" data-testid={`${testid}-editor`}>
      <div className="flex items-center justify-between">
        <div>
          <Label className="label-uppercase">Offres de remise multi-offres</Label>
          <p className="text-[11px] font-mono text-zinc-500 mt-0.5">
            Ajoute plusieurs offres distinctes. Chaque offre peut cibler un ou plusieurs services (ou tous par défaut).
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={add}
          data-testid={`${testid}-add-btn`}
        >
          <Plus size={13} className="mr-1" /> Ajouter une offre
        </Button>
      </div>

      {offers.length === 0 && (
        <div className="rounded-sm border border-dashed border-zinc-300 bg-zinc-50 px-3 py-4 text-center text-xs font-mono text-zinc-500" data-testid={`${testid}-empty`}>
          Aucune offre — la remise unique ci-dessus sera utilisée pour toutes les transactions.
        </div>
      )}

      {offers.map((o, idx) => {
        const sids = o.service_product_ids || [];
        return (
          <div
            key={idx}
            className="rounded-sm border border-zinc-200 bg-white p-3 space-y-2"
            data-testid={`${testid}-row-${idx}`}
          >
            <div className="flex items-center justify-between gap-2">
              <Badge variant="outline" className="text-[10px] font-mono">Offre #{idx + 1}</Badge>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => removeAt(idx)}
                data-testid={`${testid}-remove-${idx}`}
              >
                <Trash2 size={12} className="mr-1" /> Retirer
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div className="sm:col-span-1">
                <Label className="label-uppercase text-[10px]">Libellé (optionnel)</Label>
                <Input
                  value={o.label || ""}
                  onChange={(e) => setAt(idx, { label: e.target.value })}
                  placeholder="ex : Pack 3 mois"
                  className="h-8 text-xs"
                  data-testid={`${testid}-label-${idx}`}
                />
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">Type</Label>
                <Select
                  value={o.discount_type || "percentage"}
                  onValueChange={(v) => setAt(idx, { discount_type: v })}
                >
                  <SelectTrigger className="h-8 text-xs" data-testid={`${testid}-type-${idx}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Pourcentage (%)</SelectItem>
                    <SelectItem value="amount">Montant (€ HT)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-uppercase text-[10px]">
                  Valeur {o.discount_type === "amount" ? "(€)" : "(%)"}
                </Label>
                <div className="relative">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    max={o.discount_type === "percentage" ? "100" : undefined}
                    value={o.discount_value ?? 0}
                    onChange={(e) => setAt(idx, { discount_value: parseFloat(e.target.value) || 0 })}
                    className="h-8 text-xs pr-8"
                    data-testid={`${testid}-value-${idx}`}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none">
                    {o.discount_type === "amount" ? <Euro size={12} /> : <Percent size={12} />}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <Label className="label-uppercase text-[10px] mb-1 block">
                Services/produits éligibles
                <span className="ml-1.5 text-[10px] font-mono normal-case text-zinc-500">
                  {sids.length === 0 ? "(tous les services)" : `${sids.length} sélectionné(s)`}
                </span>
              </Label>
              {services.length === 0 ? (
                <div className="text-[11px] font-mono text-zinc-400 italic px-2 py-1">
                  Sélectionnez d'abord au moins un département pour voir les services disponibles.
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto rounded-sm border border-zinc-200 p-2 bg-zinc-50">
                  {services.map((s) => {
                    const active = sids.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleService(idx, s.id)}
                        className={
                          "inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[11px] font-mono transition-colors " +
                          (active
                            ? "border-blue-500 bg-blue-500 text-white"
                            : "border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-100")
                        }
                        data-testid={`${testid}-svc-${idx}-${s.id}`}
                      >
                        <Tag size={9} />
                        {s.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
